import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { QUERY_KEYS } from "../utils/project-constants";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from 'react-i18next';
import type { Document, DocumentFilterState, ApiResponse } from "../types/project-details.types";

export interface UseProjectDocumentsProps {
  projectId: string;
  organizationId?: string;
  filters?: DocumentFilterState;
  refreshTrigger?: number;
}

export interface UseProjectDocumentsReturn {
  documents: Document[];
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: () => void;
  uploadDocument: (file: File) => Promise<void>;
  isUploading: boolean;
  deleteDocument: (documentId: string) => void;
  isDeleting: boolean;
  updateDocumentGewerk: (documentId: string, gewerk: string | null, onComplete?: () => void) => void;
  isUpdating: boolean;
  filteredDocuments: Document[];
  totalDocuments: number;
  refreshDocuments: () => void;
}

export const useProjectDocuments = ({ 
  projectId,
  organizationId,
  filters = { searchTerm: '', sortBy: 'date', sortOrder: 'desc' },
  refreshTrigger = 0
}: UseProjectDocumentsProps): UseProjectDocumentsReturn => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation(['pages.projectDetails']);

  // Fetch documents for this project
  const { 
    data: documentsResponse, 
    error, 
    isLoading, 
    isError,
    refetch 
  } = useQuery<ApiResponse<Document[]>>({
    queryKey: [...QUERY_KEYS.projectDocuments(projectId), refreshTrigger],
    queryFn: async () => {
      const response = await apiRequest(`/api/documents/project/${projectId}?limit=200&offset=0`);
      return await response.json();
    },
    enabled: !!projectId,
  });

  const documents = documentsResponse?.data || [];

  // Upload document mutation
  const uploadDocumentMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('gewerk', 'Allgemein');

      // Use multipart upload to new endpoint
      const response = await apiRequest(`/api/projects/${projectId}/documents`, {
        method: 'POST',
        // Don't set Content-Type - browser will set it with boundary for multipart/form-data
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload file');
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate documents query
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectDocuments(projectId) });
      
      toast({
        title: t('toasts.success'),
        description: t('toasts.documentUploaded'),
      });
    },
    onError: (error) => {
      console.error('Upload document error:', error);
      toast({
        title: t('toasts.error'),
        description: t('toasts.uploadDocumentError'),
        variant: "destructive",
      });
    },
  });

  // Delete document mutation
  const deleteDocumentMutation = useMutation({
    mutationFn: async (documentId: string) => {
      // Include projectId as query parameter when deleting from project context
      const url = projectId 
        ? `/api/documents/${documentId}?projectId=${encodeURIComponent(projectId)}`
        : `/api/documents/${documentId}`;
      const response = await apiRequest(url, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete document');
      }
      
      return await response.json();
    },
    onSuccess: (data, documentId) => {
      // Remove the document from the cache
      queryClient.setQueryData(QUERY_KEYS.projectDocuments(projectId), (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.filter((doc: Document) => doc.id !== documentId)
        };
      });
      
      toast({
        title: t('toasts.success'),
        description: t('toasts.documentDeleted'),
      });
    },
    onError: (error) => {
      console.error('Delete document error:', error);
      toast({
        title: t('toasts.error'),
        description: t('toasts.deleteDocumentError'),
        variant: "destructive",
      });
    },
  });

  // Update document gewerk mutation
  const updateDocumentGewerkMutation = useMutation({
    mutationFn: async ({ documentId, gewerk }: { documentId: string; gewerk: string | null }) => {
      const response = await apiRequest(`/api/documents/${documentId}/gewerk`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ gewerk: gewerk || null }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update document gewerk');
      }
      
      return await response.json();
    },
    onMutate: async ({ documentId, gewerk }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.projectDocuments(projectId) });
      await queryClient.cancelQueries({ queryKey: ["/api/documents/project", projectId] });

      // Snapshot the previous values
      const previousDocuments = queryClient.getQueryData<ApiResponse<Document[]>>(QUERY_KEYS.projectDocuments(projectId));
      const previousDocumentsAlt = queryClient.getQueryData<ApiResponse<Document[]>>(["/api/documents/project", projectId]);

      // Optimistically update the cache for both query key formats
      const updateCache = (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((doc: Document) => 
            doc.id === documentId 
              ? { ...doc, gewerk: gewerk }
              : doc
          )
        };
      };

      if (previousDocuments?.data) {
        queryClient.setQueryData<ApiResponse<Document[]>>(QUERY_KEYS.projectDocuments(projectId), updateCache(previousDocuments));
      }
      if (previousDocumentsAlt?.data) {
        queryClient.setQueryData<ApiResponse<Document[]>>(["/api/documents/project", projectId], updateCache(previousDocumentsAlt));
      }

      // Return context with snapshots for rollback
      return { previousDocuments, previousDocumentsAlt };
    },
    onSuccess: (data, variables) => {
      // Get the updated document from the API response
      const updatedDocument = data?.data || data;
      const updatedGewerk = updatedDocument?.gewerk ?? variables.gewerk;

      // Update the document in the cache with server response for both query key formats
      const updateCache = (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((doc: Document) => 
            doc.id === variables.documentId 
              ? { ...doc, gewerk: updatedGewerk }
              : doc
          )
        };
      };

      // Update both query key formats with server response
      queryClient.setQueryData(QUERY_KEYS.projectDocuments(projectId), updateCache);
      queryClient.setQueryData(["/api/documents/project", projectId], updateCache);
      
      toast({
        title: t('doc.tradeUpdatedTitle'),
        description: t('doc.tradeUpdatedDesc'),
      });
    },
    onError: (error, variables, context) => {
      // Rollback to previous state on error
      if (context?.previousDocuments) {
        queryClient.setQueryData(QUERY_KEYS.projectDocuments(projectId), context.previousDocuments);
      }
      if (context?.previousDocumentsAlt) {
        queryClient.setQueryData(["/api/documents/project", projectId], context.previousDocumentsAlt);
      }

      console.error('Update document gewerk error:', error);
      toast({
        title: t('toasts.error'),
        description: t('doc.tradeUpdateFailed', { message: error.message }),
        variant: "destructive",
      });
    },
  });

  // Filter and sort documents
  const filteredDocuments = documents
    .filter(doc => {
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        return (
          doc.name?.toLowerCase().includes(searchLower) ||
          doc.fileName?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (filters.sortBy) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'date':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'size':
          aValue = a.fileSize || 0;
          bValue = b.fileSize || 0;
          break;
        default:
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
      }
      
      if (aValue < bValue) return filters.sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return filters.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  const uploadDocument = async (file: File) => {
    return uploadDocumentMutation.mutateAsync(file);
  };

  const deleteDocument = (documentId: string) => {
    deleteDocumentMutation.mutate(documentId);
  };

  const updateDocumentGewerk = (documentId: string, gewerk: string | null, onComplete?: () => void) => {
    updateDocumentGewerkMutation.mutate(
      { documentId, gewerk },
      {
        onSettled: () => {
          onComplete?.();
        },
      }
    );
  };

  const refreshDocuments = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectDocuments(projectId) });
  };

  return {
    documents,
    isLoading,
    error: error as Error | null,
    isError,
    refetch,
    uploadDocument,
    isUploading: uploadDocumentMutation.isPending,
    deleteDocument,
    isDeleting: deleteDocumentMutation.isPending,
    updateDocumentGewerk,
    isUpdating: updateDocumentGewerkMutation.isPending,
    filteredDocuments,
    totalDocuments: documents.length,
    refreshDocuments,
  };
};
